import EventEmitter from 'eventemitter3';
import styles from './styles.module.scss';

export type Token = {
  '--theme-text'?: string;
  '--theme-text-hover'?: string;
  '--theme-background'?: string;
  '--theme-background-hover'?: string;
  '--theme-background-select'?: string;
  '--theme-border'?: string;
  '--theme-kline-text'?: string;
  '--theme-kline-text-hover'?: string;
  '--theme-dropdown'?: string;
  '--theme-kline'?: string;
  '--theme-default-dropdown'?: string;
  '--theme-selected-dropdown'?: string;
};

type Option = {
  theme: 'default' | 'kline' | 'kline-legend';
  data: { index?: number; key: string; text: string }[];
  defaultValue: string;
  listMaxHeight?: number;
  /** 只有一条数据时自动隐藏下拉列表 */
  listAutoHide?: boolean;
  /** 下拉列表使用固定布局，防止容器出现滚动条 */
  listPositionFixed?: boolean;
  /** 主题token */
  token?: Token;
};

type EventTypes = {
  change: (dataItem: Option['data'][number]) => void;
};

export default class Dropdown extends EventEmitter<EventTypes> {
  private __$dom: HTMLElement;

  private __option: Option;

  private __value!: Option['data'][number]['key'];

  private __$container!: HTMLElement;

  private __index: Option['data'][number]['index'] = 0;

  private __$optionList!: HTMLElement;

  constructor($wrapper: HTMLElement, option: Option) {
    super();

    this.__option = option;
    this.__option.theme = this.__option.theme || 'default';
    this.__value = option.defaultValue;
    this.__$dom = $wrapper;

    this.__initialize();
    this.__initTheme(this.__$dom);
    this.__initTheme(this.__$optionList);

    this.__bindEvents();
    this.__refresh(false);
  }

  /** 初始化主题 */
  private __initTheme($dom: HTMLElement) {
    const themeConfig = this.__option.token;
    if (!themeConfig) {
      return;
    }

    Object.keys(themeConfig).forEach(key => {
      $dom.style.setProperty(key, themeConfig[key]);
    });
  }

  private __initialize() {
    this.__$dom.classList.add(styles.wrapper, styles['theme-container']);

    const $container = document.createElement('div');
    $container.classList.add(styles.container, styles[`theme-${this.__option.theme}`]);

    const $selector = document.createElement('section');
    const $optionList = document.createElement('section');

    $selector.classList.add('js-dropdown-selector', styles.selector);
    $optionList.classList.add(
      'js-dropdown-option-list',
      styles[`theme-${this.__option.theme}`],
      styles['option-list'],
      styles['theme-container']
    );
    $optionList.style.maxHeight = `${this.__option.listMaxHeight || 160}px`;

    let result = '';

    if (!this.__option.listAutoHide || this.__option.data.length > 1) {
      if (this.__option.theme === 'default') {
        result = `<div class=${styles['default-dropdown']}></div>`;
      } else {
        result = `<div class=${styles.dropdown}></div>`;
      }
    }
    $selector.innerHTML = `
      ${this.__option.theme === 'kline-legend' ? `<div class=${styles.kline}></div>` : ''}
      <span class="js-value-text ${styles['value-text']}"></span>
      ${result}
    `;

    $optionList.innerHTML = this.__option.data
      .map(item => `<div data-value="${item.key}"><svg></svg>${item.text}</div>`)
      .join('');

    $container.appendChild($selector);
    $container.appendChild($optionList);
    this.__$dom.appendChild($container);

    this.__$container = $container;
    this.__$optionList = $optionList;
  }

  private __bindEvents() {
    const self = this;

    self.__$container.querySelector('.js-dropdown-selector')!.addEventListener('click', () => {
      if (self.__option.listAutoHide && self.__option.data.length <= 1) {
        return;
      }

      self.__refresh(!self.__$container.classList.contains(styles['is-active']));
    });

    self.__$optionList.addEventListener('click', event => {
      const value = (event.target as HTMLElement).getAttribute('data-value');
      const index = self.__option.data.findIndex(item => item.key === value);

      if (!value) {
        return;
      }

      self.__index = index;
      self.__value = value;
      self.__refresh(false, true);
    });
  }

  private getCurrentIndex() {
    return this.__index;
  }

  private __refresh(isActive: boolean, triggerEvent = false) {
    const currentDataItem = this.__option.data.find(item => item.key === this.__value);
    const $optionList = this.__$optionList;

    Array.from($optionList.children).forEach(el => {
      const value = el.getAttribute('data-value');

      el.classList[this.__value === value ? 'add' : 'remove'](styles['is-selected']);
    });

    this.__$container.querySelector('.js-value-text')!.textContent = currentDataItem!.text;

    if (isActive && this.__option.listPositionFixed) {
      const bBox = this.__$container
        .querySelector('.js-dropdown-selector')!
        .getBoundingClientRect();

      $optionList.style.position = 'fixed';
      $optionList.style.top = `${bBox.bottom}px`;
      $optionList.style.left = `${bBox.left}px`;
      $optionList.style.right = 'initial';
      $optionList.style.bottom = 'initial';

      $optionList.parentElement?.removeChild($optionList);
      document.body.appendChild($optionList);
    } else {
      $optionList.style.position = 'absolute';
      $optionList.style.top = '100%';
      $optionList.style.left =
        !this.__option.theme || this.__option.theme === 'default' ? 'initial' : '0';
      $optionList.style.right =
        !this.__option.theme || this.__option.theme === 'default' ? '0' : 'initial';
      $optionList.style.bottom = 'initial';
    }

    $optionList.style.display = isActive ? 'block' : 'none';

    this.__$container.classList[isActive ? 'add' : 'remove'](styles['is-active']);

    if (triggerEvent) {
      this.emit('change', currentDataItem!);
    }
  }

  destroy() {
    this.__$optionList.parentElement?.removeChild(this.__$optionList);
    this.__$dom.classList.remove(styles.wrapper);
    this.__$dom.innerHTML = '';
  }
}
